import type { GlobalPageCard } from '@confluence/page-card/entry-points/cardTypes.ts';
import { transformGlobalContent } from '@confluence/page-card/entry-points/transformers.ts';

import type { EndOfPageRecommendationTwgQuery$data } from './__generated__/EndOfPageRecommendationTwgQuery.graphql.ts';

// Strategy label for the TWG arm (TWG has no `strategy`), for analytics parity.
export const TWG_STRATEGY = 'TWG';

// Inline-hydrated content node. The graph relationship returns both pages and blog
// posts, so `data` selects `... on ConfluencePage` and `... on ConfluenceBlogPost`.
// Relay types a multi-fragment union field awkwardly for direct field access, so we
// read it through this hand-typed shape (a superset of both fragments' selections).
// `pageId` is present for pages, `blogPostId` for blog posts.
type TwgContentNode = {
	pageId?: string | null;
	blogPostId?: string | null;
	title?: string | null;
	subtype?: string | null;
	// `status` is aliased per type in the query (ConfluencePage.status and
	// ConfluenceBlogPost.status are different enum types); read whichever is present.
	pageStatus?: string | null;
	blogPostStatus?: string | null;
	links?: { webUi?: string | null } | null;
	space?: {
		id?: string | null;
		key?: string | null;
		alias?: string | null;
		name?: string | null;
	} | null;
	owner?: { user?: { name?: string | null; accountId?: string | null } | null } | null;
	properties?: ReadonlyArray<{ key: string; value: string }> | null;
};

const contentIdOf = (node: TwgContentNode): string | undefined =>
	node.pageId ?? node.blogPostId ?? undefined;

// Authority score, ranked highest-first for sorting.
type AuthorityScore = 'VERY_HIGH' | 'HIGH' | 'MEDIUM' | 'LOW';
const AUTHORITY_SCORE_RANK: Record<AuthorityScore, number> = {
	VERY_HIGH: 3,
	HIGH: 2,
	MEDIUM: 1,
	LOW: 0,
};
const DEFAULT_AUTHORITY_SCORE: AuthorityScore = 'LOW';

// RETURN column names from `buildTwgRecsCypher`.
const ENTITY_COLUMN_KEY = 'entity';
const AUTHORITY_COLUMN_KEY = 'auth';

// Score is the last segment of the auth node's ARI (`.../authority-score/MEDIUM`);
// missing/unrecognised falls back to LOW so ranking is always defined.
const parseAuthorityScore = (authAri: string | null | undefined): AuthorityScore => {
	const score = authAri?.split('/').pop()?.toUpperCase();
	return score && score in AUTHORITY_SCORE_RANK
		? (score as AuthorityScore)
		: DEFAULT_AUTHORITY_SCORE;
};

export type RankedTwgContentNode = TwgContentNode & { authorityScore: AuthorityScore };

// Content statuses that cc-smarts would never recommend. We filter these out of the
// TWG results so treatment doesn't surface drafts/archived/trashed/etc. that control
// can't (the graph traversal isn't status-filtered, unlike the cc-smarts backend).
const NON_CURRENT_STATUSES = new Set(['archived', 'deleted', 'draft', 'historical', 'trashed']);

// Keep CURRENT and unknown/absent status (ConfluenceBlogPost.status is nullable and may
// not hydrate), dropping only definitively non-current content. Lenient on purpose so a
// blog whose status doesn't resolve isn't silently dropped.
const isCurrentContent = (node: TwgContentNode): boolean => {
	const status = node.pageStatus ?? node.blogPostStatus;
	return !status || !NON_CURRENT_STATUSES.has(String(status).toLowerCase());
};

/**
 * De-duplicated content nodes (pages + blog posts) from a `graphStore_cypherQueryV2`
 * result, ranked by authority score (traversal order as the tiebreaker within a bucket)
 * and filtered of non-current content for parity with the cc-smarts control arm.
 */
export const extractTwgRecContent = (
	data: EndOfPageRecommendationTwgQuery$data | null | undefined,
): RankedTwgContentNode[] => {
	const seen = new Set<string>();
	const nodes: RankedTwgContentNode[] = [];

	// Each edge is one row; iterate per-row so the `entity` and `auth` columns stay paired.
	const edges = data?.graphStore_cypherQueryV2?.edges ?? [];
	for (const edge of edges) {
		let node: TwgContentNode | null | undefined;
		let authorityScore: AuthorityScore = DEFAULT_AUTHORITY_SCORE;
		// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity -- bounded by RETURN column count
		for (const column of edge?.node?.columns ?? []) {
			if (column?.key === AUTHORITY_COLUMN_KEY) {
				authorityScore = parseAuthorityScore(column?.value?.id);
			} else if (column?.key === ENTITY_COLUMN_KEY) {
				// Relay's multi-fragment union type doesn't allow direct field access.
				node = column?.value?.data as TwgContentNode | null | undefined;
			}
		}

		const contentId = node ? contentIdOf(node) : undefined;
		if (node && contentId && isCurrentContent(node) && !seen.has(contentId)) {
			seen.add(contentId);
			nodes.push({ ...node, authorityScore });
		}
	}

	// Stable sort (ES2019+): equal-score nodes keep their traversal order.
	return [...nodes].sort(
		(a, b) => AUTHORITY_SCORE_RANK[b.authorityScore] - AUTHORITY_SCORE_RANK[a.authorityScore],
	);
};

// Map an AGG content node onto the `content` shape transformGlobalContent expects.
// Renames: webUi->webui, subtype->subType, owner.user.name->displayName,
// owner.user.accountId->creatorId, properties->emoji.nodes. `type` is derived from
// which id is present (the AGG enum is `PAGE`/`BLOG_POST`, which wouldn't match the
// card's lower-case, no-underscore content types and would render the wrong icon).
const toGlobalContentShape = (node: TwgContentNode) => {
	const status = node.pageStatus ?? node.blogPostStatus;
	return {
		id: contentIdOf(node) ?? '',
		title: node.title ?? '',
		type: node.blogPostId ? 'blogpost' : 'page',
		subType: node.subtype ?? undefined,
		status: status ? String(status).toLowerCase() : undefined,
		links: {
			webui: node.links?.webUi ?? '',
		},
		space: {
			id: node.space?.id ?? '',
			key: node.space?.key ?? '',
			alias: node.space?.alias ?? '',
			name: node.space?.name ?? '',
		},
		creatorId: node.owner?.user?.accountId ?? '',
		history: {
			ownedBy: {
				displayName: node.owner?.user?.name ?? '',
			},
		},
		emoji: {
			nodes: node.properties ?? [],
		},
	};
};

/**
 * Adapts a TWG result into the `GlobalPageCard[]` shape the shared display
 * component consumes, preserving TWG rank order and matching the cc-smarts arm.
 */
export const transformTwgRecs = (
	data: EndOfPageRecommendationTwgQuery$data | null | undefined,
): GlobalPageCard[] => {
	return extractTwgRecContent(data).map((node) => {
		const card = transformGlobalContent(toGlobalContentShape(node));
		// Parity with the cc-smarts transformer: zero these + stamp a strategy.
		card.likesCount = 0;
		card.commentsCount = 0;
		card.recommendationStrategy = TWG_STRATEGY;
		return card;
	});
};
